import { useRef, useState } from 'react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { formatCentsToCurrency } from '@frontend/currency';
import { useTranslation } from '@frontend/i18n';
import { EmailView, useEmailStates } from '@frontend/integrated-messaging';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Text, formatDate, useAlert } from '@frontend/design-system';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { PAYMENT_MULTI_STEP_LABELS, PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { StyledStep } from '../../atoms/styled-step';
import { ShareInEmailFooter } from './share-in-email-footer';
import { ShareInEmailSelectTemplateFooter } from './share-in-email-select-template-footer';
import { ShareInEmailStepBodyProps } from './share-in-email.types';

export const ShareInEmailBody = ({ onGoBack, onCompleteStep }: ShareInEmailStepBodyProps) => {
  const {
    invoice,
    multiStep: { onCancel },
  } = useCollectPaymentMultiContext();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });

  const alert = useAlert();
  const { navigate } = useSettingsNavigate();

  const { t } = useTranslation('payments-share-in-email');
  const [isSending, setIsSending] = useState(false);
  const { getLocationName } = useAppScopeStore();
  const goBackOnSubmit = useRef(false);

  const templateType = TemplateType_Slug.MANUAL_PAYMENT_REQUEST;

  const emailStates = useEmailStates({
    locationId: invoice?.locationId ?? '',
    templateType,
    linkData: {
      link: invoice?.links.payment ?? '',
      relatedId: invoice?.id ?? '',
    },
    personId: invoice?.person.id ?? '',
    onSendSuccess: () => {
      if (goBackOnSubmit.current) {
        onGoBack();
      } else {
        onCompleteStep();
      }

      alert.success('Email sent successfully');
      setIsSending(false);
    },
    onSendError: () => {
      setIsSending(false);
    },
    initialValues: {
      subject: t('Balance due for {{name}}', { name: invoice ? getLocationName(invoice.locationId) : '' }),
    },
    onSettingsOpen: () => {
      onCancel?.();
    },
  });

  const { primaryTrackingId, backTrackingId, newTemplateTrackingId, newTemplateBackId } = useScopedTrackingIds({
    primaryTrackingId: `${PAYMENT_MULTI_STEPS.shareInEmail}--send-now-btn`,
    backTrackingId: `${PAYMENT_MULTI_STEPS.shareInEmail}--back-btn`,
    newTemplateTrackingId: `${PAYMENT_MULTI_STEPS.shareInEmail}--new-template-btn`,
    newTemplateBackId: `${PAYMENT_MULTI_STEPS.shareInEmail}--new-template-back-btn`,
  });

  if (!invoice) return null;

  const isTemplateSelected = !!emailStates.templateSelection[0];

  const handleSettingsNavigate: typeof navigate = (args) => {
    setTimeout(() => {
      navigate({ ...args, search: { ...args.search, closeOnSave: true, closeOnCancel: true } });
    }, 400);
  };

  const onClickNewTemplate = () => {
    onCancel?.();
    handleSettingsNavigate({
      to: '/messaging/templates/create',
    });
  };

  const footerComponents = isTemplateSelected
    ? {
        Footer: () => (
          <ShareInEmailSelectTemplateFooter
            onGoBack={onGoBack}
            onClickNewTemplate={onClickNewTemplate}
            backTrackingId={newTemplateBackId}
            newTemplateTrackingId={newTemplateTrackingId}
          />
        ),
      }
    : {
        Footer: () => (
          <ShareInEmailFooter
            onGoBack={onGoBack}
            disabled={!emailStates.toField.value.length || isSending}
            showDisabledTooltip={!emailStates.toField.value.length}
            backTrackingId={backTrackingId}
            primaryTrackingId={primaryTrackingId}
            onSubmit={(e) => {
              if (!e) return;
              setIsSending(true);
              emailStates.formProps.onSubmit(e);
            }}
          />
        ),
      };

  return (
    <StyledStep
      noPaddingOnBody={isMobile}
      id={PAYMENT_MULTI_STEPS.shareInEmail}
      label={PAYMENT_MULTI_STEP_LABELS[PAYMENT_MULTI_STEPS.shareInEmail]}
      components={footerComponents}
    >
      <form {...emailStates.formProps}>
        <EmailView
          states={emailStates}
          hideNewTemplateButton={true}
          contextPreviewProps={{
            iconName: 'dollar-sign',
            title: (
              <Text size={isMobile ? 'medium' : 'large'} weight='bold'>
                {formatCentsToCurrency(invoice?.billedAmount)}
              </Text>
            ),
            subtitle: (
              <Text size={isMobile ? 'small' : 'large'} color='light'>
                {t('Due')}:{' '}
                <Text as='span' color='light' size={isMobile ? 'small' : 'large'}>
                  {formatDate(invoice?.billedAt, 'MMM D, YYYY')}
                </Text>
              </Text>
            ),
          }}
        />
      </form>
    </StyledStep>
  );
};
